// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { noteSchema } from '@/note/api/types';
import { z } from 'zod';

/**
 * Schema for validating the response from the update note API endpoint.
 * Includes both the updated note data and metadata about the request.
 */
const updateNoteResponseSchema = z
  .object({
    data: noteSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, note: data }));

/**
 * Type definition for the update note API response.
 * Contains the updated note data and request metadata.
 */
type UpdateNoteResponse = z.infer<typeof updateNoteResponseSchema>;

/**
 * Parameters for updating a note.
 * All fields are required.
 *
 * @property content - The content of the note
 */
interface UpdateNoteParameters {
  content: string;
}

/**
 * Update a note
 * @param noteKey The key of the note to update
 * @param request The request containing the updated note content
 * @returns A promise that resolves to the update note response
 */
export async function updateNote(
  noteKey: string,
  request: UpdateNoteParameters
): Promise<UpdateNoteResponse> {
  const url = `/v1/note/${noteKey}`;
  const response = await api.patch(url, request);

  return parseResponse(updateNoteResponseSchema, response.data, {
    schemaName: 'UpdateNote',
    endpoint: url,
  });
}
